// src/pages/StudyNotebook.jsx
// Dashboard page that aggregates all highlights and study notes across the entire library.
// Provides search, filtering, inline editing, and markdown exporting.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function StudyNotebook({ books }) {
  const navigate = useNavigate();
  const { user, syncToCloud } = useAuth();

  // State
  const [annotations, setAnnotations] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedBookId, setSelectedBookId] = useState("All");
  const [selectedType, setSelectedType] = useState("All"); // All, highlight, note
  const [sortBy, setSortBy] = useState("newest"); // newest, oldest, page
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState("");

  // Load annotations from localStorage on mount/books list load
  const loadAnnotations = useCallback(() => {
    const list = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("reader_annotations_")) {
          const bookId = key.replace("reader_annotations_", "");
          const book = books.find((b) => b.id === bookId);
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              parsed.forEach((ann) => {
                list.push({
                  ...ann,
                  bookId,
                  bookTitle: book?.title || "Unknown Book",
                  bookAuthor: book?.author || "Unknown Author",
                  bookColorFrom: book?.coverColor?.[0] || book?.colorFrom || "#1a1208",
                  bookColorTo: book?.coverColor?.[1] || book?.colorTo || "#8b4513",
                  bookGenre: book?.genre || "Unknown Genre",
                });
              });
            }
          }
        }
      }
    } catch (e) {
      console.error("Error loading annotations from localStorage:", e);
    }
    setAnnotations(list);
  }, [books]);

  useEffect(() => {
    loadAnnotations();
  }, [loadAnnotations]);



  // Extract unique books that have annotations for the filter dropdown
  const annotatedBooks = useMemo(() => {
    const map = new Map();
    annotations.forEach((ann) => {
      if (!map.has(ann.bookId)) {
        map.set(ann.bookId, ann.bookTitle);
      }
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [annotations]);

  // Filter & Sort logic
  const filtered = useMemo(() => {
    let result = [...annotations];

    // Search query
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (ann) =>
          ann.text?.toLowerCase().includes(q) ||
          ann.note?.toLowerCase().includes(q) ||
          ann.bookTitle?.toLowerCase().includes(q)
      );
    }

    // Book Filter
    if (selectedBookId !== "All") {
      result = result.filter((ann) => ann.bookId === selectedBookId);
    }

    // Type Filter
    if (selectedType !== "All") {
      result = result.filter((ann) => ann.type === selectedType);
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === "newest") {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (sortBy === "oldest") {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      if (sortBy === "page") {
        // Sort by book title first, then page number
        const titleCompare = a.bookTitle.localeCompare(b.bookTitle);
        if (titleCompare !== 0) return titleCompare;
        return a.page - b.page;
      }
      return 0;
    });

    return result;
  }, [annotations, search, selectedBookId, selectedType, sortBy]);

  // Checkbox Selection Helpers
  const handleSelectToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllToggle = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((ann) => ann.id));
    }
  };

  // Delete individual annotation
  const handleDelete = (bookId, id) => {
    if (!window.confirm("Are you sure you want to delete this study note/highlight?")) return;
    try {
      const key = `reader_annotations_${bookId}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        const updated = parsed.filter((ann) => ann.id !== id);
        if (updated.length === 0) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(updated));
        }
        localStorage.setItem("ebenezer_progress_last_updated", String(Date.now()));
        if (user) {
          syncToCloud(user).catch((err) => console.error("[StudyNotebook] Background sync failed:", err));
        }
        loadAnnotations();
        setSelectedIds((prev) => prev.filter((x) => x !== id));
      }
    } catch (e) {
      console.error("Error deleting annotation:", e);
    }
  };

  // Delete selected annotations
  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete the ${selectedIds.length} selected notes?`)) return;

    try {
      selectedIds.forEach((id) => {
        const target = annotations.find((ann) => ann.id === id);
        if (target) {
          const key = `reader_annotations_${target.bookId}`;
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            const updated = parsed.filter((ann) => ann.id !== id);
            if (updated.length === 0) {
              localStorage.removeItem(key);
            } else {
              localStorage.setItem(key, JSON.stringify(updated));
            }
          }
        }
      });
      localStorage.setItem("ebenezer_progress_last_updated", String(Date.now()));
      if (user) {
        syncToCloud(user).catch((err) => console.error("[StudyNotebook] Background sync failed:", err));
      }
      loadAnnotations();
      setSelectedIds([]);
    } catch (e) {
      console.error("Error deleting selected annotations:", e);
    }
  };

  // Inline Note Editor
  const startEditing = (id, note) => {
    setEditingId(id);
    setEditVal(note || "");
  };

  const saveEdit = (bookId, id) => {
    try {
      const key = `reader_annotations_${bookId}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        const updated = parsed.map((ann) =>
          ann.id === id ? { ...ann, note: editVal.trim(), type: editVal.trim() ? "note" : "highlight" } : ann
        );
        localStorage.setItem(key, JSON.stringify(updated));
        localStorage.setItem("ebenezer_progress_last_updated", String(Date.now()));
        if (user) {
          syncToCloud(user).catch((err) => console.error("[StudyNotebook] Background sync failed:", err));
        }
        loadAnnotations();
        setEditingId(null);
      }
    } catch (e) {
      console.error("Error saving annotation edit:", e);
    }
  };

  // Jump to specific book page
  const handleJumpToPage = (bookId, page) => {
    localStorage.setItem(`bookmark_${bookId}`, String(page));
    localStorage.setItem("ebenezer_progress_last_updated", String(Date.now()));
    if (user) {
      syncToCloud(user).catch((err) => console.error("[StudyNotebook] Background sync failed:", err));
    }
    navigate(`/book/${bookId}/read`);
  };

  // Exporters
  const exportToMarkdown = () => {
    const targets = selectedIds.length > 0 ? filtered.filter((x) => selectedIds.includes(x.id)) : filtered;
    if (targets.length === 0) {
      alert("No notes found to export.");
      return;
    }

    let md = `# Ebenezer Books — Study Notebook\n\n`;
    md += `Exported on ${new Date().toLocaleDateString()} · total entries: ${targets.length}\n\n---\n\n`;

    // Group by book title
    const groups = {};
    targets.forEach((ann) => {
      if (!groups[ann.bookTitle]) groups[ann.bookTitle] = [];
      groups[ann.bookTitle].push(ann);
    });

    Object.entries(groups).forEach(([title, items]) => {
      md += `## 📚 ${title}\n\n`;
      items.forEach((item, index) => {
        const dateStr = new Date(item.createdAt).toLocaleDateString();
        md += `### Entry ${index + 1} — Page ${item.page} (${dateStr})\n`;
        md += `> ${item.text}\n\n`;
        if (item.note) {
          md += `**My Study Notes:**\n${item.note}\n\n`;
        }
        md += `* * *\n\n`;
      });
    });

    const blob = new Blob([md], { type: "text/markdown;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `ebenezer_study_notes_${new Date().toISOString().slice(0,10)}.md`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToText = () => {
    const targets = selectedIds.length > 0 ? filtered.filter((x) => selectedIds.includes(x.id)) : filtered;
    if (targets.length === 0) {
      alert("No notes found to export.");
      return;
    }

    let txt = `==================================================\n`;
    txt += `EBENEZER BOOKS — PERSONAL STUDY NOTEBOOK\n`;
    txt += `Exported: ${new Date().toLocaleString()} | Count: ${targets.length}\n`;
    txt += `==================================================\n\n`;

    const groups = {};
    targets.forEach((ann) => {
      if (!groups[ann.bookTitle]) groups[ann.bookTitle] = [];
      groups[ann.bookTitle].push(ann);
    });

    Object.entries(groups).forEach(([title, items]) => {
      txt += `📚 BOOK: ${title}\n`;
      txt += `--------------------------------------------------\n\n`;
      items.forEach((item, index) => {
        txt += `[Page ${item.page} | ${new Date(item.createdAt).toLocaleDateString()}]\n`;
        txt += `Highlight: "${item.text}"\n`;
        if (item.note) {
          txt += `Study Notes: ${item.note}\n`;
        }
        txt += `\n`;
      });
      txt += `\n`;
    });

    const blob = new Blob([txt], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `ebenezer_study_notes_${new Date().toISOString().slice(0,10)}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{
      color: "var(--ink)",
      width: "100%",
    }}>
      {/* Styles Injection */}
      <style>{`
        .notebook-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1.5rem;
        }
        .notebook-title {
          font-family: var(--font-display);
          font-size: clamp(1.8rem, 3.5vw, 2.5rem);
          font-weight: 600;
        }
        .filter-capsule {
          background: var(--white);
          border: 1px solid var(--dust);
          border-radius: 30px;
          padding: 6px 14px;
          font-family: var(--font-body);
          font-size: 0.85rem;
          color: var(--ink);
          outline: none;
          cursor: pointer;
          transition: border-color 0.2s;
          max-width: 100%;
          box-sizing: border-box;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }
        body.dark-theme .filter-capsule {
          background: rgba(18, 30, 44, 0.6);
          border-color: rgba(255, 255, 255, 0.08);
        }
        .filter-capsule:focus {
          border-color: var(--gold);
        }
        .filters-row {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          align-items: center;
        }
        .filters-row input[type="text"].filter-capsule {
          width: 200px;
        }
        .notebook-actions-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: var(--white);
          border: 1px solid var(--dust);
          border-radius: 12px;
          padding: 0.75rem 1.25rem;
          margin-bottom: 1.5rem;
          gap: 1rem;
        }
        body.dark-theme .notebook-actions-bar {
          background: rgba(18, 30, 44, 0.4);
          border-color: rgba(255, 255, 255, 0.08);
        }
        .notebook-actions-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .action-btn-styled {
          background: transparent;
          border: 1px solid var(--dust);
          color: var(--ink);
          font-family: var(--font-body);
          font-size: 0.82rem;
          font-weight: 600;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .action-btn-styled:hover {
          border-color: var(--gold);
          color: var(--gold);
          box-shadow: 0 2px 8px rgba(200, 146, 42, 0.1);
        }
        .action-btn-styled.primary {
          background: var(--gold);
          border-color: var(--gold);
          color: var(--white);
        }
        .action-btn-styled.primary:hover {
          background: var(--gold-hover);
          border-color: var(--gold-hover);
          color: var(--white);
          box-shadow: 0 4px 12px rgba(200, 146, 42, 0.25);
        }
        .action-btn-styled.danger:hover {
          border-color: var(--rust);
          color: var(--rust);
          box-shadow: 0 2px 8px rgba(139, 58, 42, 0.1);
        }
        
        /* Note Cards */
        .note-card {
          background: var(--white);
          border: 1px solid var(--dust);
          border-radius: 16px;
          padding: 1.5rem;
          display: flex;
          gap: 1.25rem;
          position: relative;
          transition: all 0.3s;
          margin-bottom: 1.25rem;
          word-break: break-word;
          overflow-wrap: break-word;
          box-shadow: var(--shadow);
        }
        body.dark-theme .note-card {
          background: var(--white);
          border-color: var(--dust);
        }
        .note-card:hover {
          box-shadow: var(--shadow-lg);
          border-color: var(--gold);
        }
        
        .card-left-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          flex-shrink: 0;
        }
        .card-content-col {
          flex: 1;
          min-width: 0;
        }
        .note-checkbox {
          width: 18px;
          height: 18px;
          accent-color: var(--gold);
          cursor: pointer;
        }
        .card-cover-side {
          width: 48px;
          height: 64px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-display);
          font-weight: 700;
          color: #fff;
          font-size: 0.65rem;
          text-align: center;
          padding: 4px;
          flex-shrink: 0;
          box-shadow: 0 4px 10px rgba(0,0,0,0.15);
        }
        
        .card-header-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .card-meta-info {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 6px;
        }
        .card-meta-text {
          color: var(--muted);
          font-size: 0.82rem;
          margin-left: 8px;
        }
        .card-badge-row {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        .card-type-badge {
          font-size: 0.68rem;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 10px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .card-date-text {
          color: var(--muted);
          font-size: 0.72rem;
        }
        
        .quote-container {
          margin: 0.75rem 0;
          font-size: 0.95rem;
          padding: 0.6rem 1rem;
          background: rgba(128, 128, 128, 0.04);
          border-left: 2.5px solid var(--dust);
          border-radius: 0 8px 8px 0;
          line-height: 1.5;
          font-style: italic;
          color: var(--ink);
          word-break: break-word;
          overflow-wrap: break-word;
        }
        body.dark-theme .quote-container {
          background: rgba(255, 255, 255, 0.03);
        }
        
        .thoughts-container {
          margin-top: 0.6rem;
          background: rgba(128, 128, 128, 0.03);
          border-radius: 8px;
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
          line-height: 1.5;
          border-left: 2.5px solid var(--gold);
          color: var(--ink);
        }
        body.dark-theme .thoughts-container {
          background: rgba(255, 255, 255, 0.02);
        }
        
        .write-note-link {
          font-size: 0.82rem;
          color: var(--gold);
          cursor: pointer;
          text-decoration: underline;
          font-weight: 500;
        }
        
        .edit-container {
          margin-top: 0.5rem;
        }
        .edit-textarea {
          width: 100%;
          min-height: 80px;
          border: 1px solid var(--dust);
          background: var(--cream);
          color: var(--ink);
          font-family: var(--font-body);
          font-size: 0.9rem;
          padding: 0.5rem;
          border-radius: 8px;
          resize: vertical;
          outline: none;
          margin-bottom: 0.5rem;
        }
        .edit-textarea:focus {
          border-color: var(--gold);
        }
        
        .card-footer-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 1rem;
          border-top: 1px dashed var(--dust);
          padding-top: 0.6rem;
          flex-wrap: wrap;
        }
        .card-action-link {
          background: none;
          border: none;
          color: var(--muted);
          font-size: 0.78rem;
          cursor: pointer;
          font-weight: 600;
          padding: 4px 0;
          font-family: var(--font-body);
          transition: color 0.2s;
        }
        .card-action-link:hover {
          color: var(--gold);
        }
        .card-action-link.danger:hover {
          color: var(--rust);
        }
        
        /* Responsive Media Queries */
        @media (max-width: 768px) {
          .notebook-header {
            flex-direction: column;
            align-items: stretch !important;
            gap: 1.25rem !important;
            margin-bottom: 1.5rem !important;
          }
          .filters-row {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 0.5rem;
            width: 100%;
          }
          .filters-row input[type="text"].filter-capsule {
            grid-column: span 2;
            width: 100% !important;
          }
          .filter-capsule {
            width: 100% !important;
            max-width: 100%;
          }
        }
        @media (max-width: 576px) {
          .note-card {
            padding: 1rem;
            gap: 0.75rem;
          }
          .card-left-col {
            gap: 0;
          }
          .card-cover-side {
            display: none !important;
          }
          .card-meta-text {
            display: block;
            margin-left: 0 !important;
            width: 100%;
            margin-top: 2px;
          }
          .card-header-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }
          .card-badge-row {
            width: 100%;
            justify-content: space-between;
          }
          .notebook-actions-bar {
            flex-direction: column;
            align-items: flex-start;
            padding: 0.8rem;
          }
          .notebook-actions-buttons {
            width: 100%;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.5rem;
          }
          .notebook-actions-buttons .action-btn-styled {
            width: 100%;
            justify-content: center;
            font-size: 0.76rem;
            padding: 8px 6px;
          }
          .notebook-actions-buttons .action-btn-styled.danger {
            grid-column: span 2;
          }
          .card-footer-actions {
            justify-content: flex-start;
            gap: 12px;
          }
        }
      `}</style>


      {/* ─── HEADER ─── */}
      <div className="notebook-header">
        <div style={{ flex: 1, minWidth: "250px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <h2 style={{
              fontFamily: "var(--font-display)",
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "var(--ink)",
              margin: 0,
            }}>Study Notebook & Highlights</h2>
            <div style={{
              height: "1px",
              flex: 1,
              background: "linear-gradient(90deg, var(--dust), transparent)",
            }} />
          </div>
          <p style={{ color: "var(--muted)", fontSize: "0.85rem", marginTop: "4px" }}>
            Aggregate, search, and export saved highlights and notes across your library.
          </p>
        </div>

        {/* Filters and Inputs grid/flex */}
        <div className="filters-row">
          {/* Keyword Search */}
          <input
            type="text"
            placeholder="Search text or notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="filter-capsule"
          />

          {/* Book Filter */}
          <select
            value={selectedBookId}
            onChange={(e) => setSelectedBookId(e.target.value)}
            className="filter-capsule"
          >
            <option value="All">All Books</option>
            {annotatedBooks.map((ab) => (
              <option key={ab.id} value={ab.id}>{ab.title}</option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-capsule"
          >
            <option value="All">All Annotations</option>
            <option value="highlight">Highlights Only</option>
            <option value="note">Notes Only</option>
          </select>

          {/* Sort selection */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-capsule"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="page">Page Order</option>
          </select>
        </div>
      </div>

      {/* Empty State */}
      {annotations.length === 0 ? (
        <div className="glass-panel" style={{
          textAlign: "center",
          padding: "5rem 2rem",
          color: "var(--muted)",
        }}>
          <div style={{ fontSize: "4.5rem", marginBottom: "1rem" }}>✍️</div>
          <h2 style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.8rem", color: "var(--ink)", marginBottom: "0.5rem"
          }}>Your Study Notebook is empty</h2>
          <p style={{ maxWidth: "500px", margin: "0 auto 1.5rem", lineHeight: 1.6 }}>
            Start reading any volume in the reader, select passages to highlight, and add study notes to compile your personal search-ready theological notes repository.
          </p>
          <button
            onClick={() => navigate("/")}
            className="action-btn-styled primary"
          >
            Browse Library Catalogue
          </button>
        </div>
      ) : (
        <>
          {/* ─── ACTIONS BAR ─── */}
          <div className="notebook-actions-bar">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                type="checkbox"
                className="note-checkbox"
                checked={filtered.length > 0 && selectedIds.length === filtered.length}
                onChange={handleSelectAllToggle}
                title="Select all filtered notes"
              />
              <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                {selectedIds.length > 0
                  ? `${selectedIds.length} of ${filtered.length} selected`
                  : `Select items to export (${filtered.length} total)`
                }
              </span>
            </div>

            <div className="notebook-actions-buttons">
              <button
                className="action-btn-styled primary"
                onClick={exportToMarkdown}
              >
                📥 Export Markdown
              </button>
              <button
                className="action-btn-styled"
                onClick={exportToText}
              >
                📄 Export Text
              </button>
              {selectedIds.length > 0 && (
                <button
                  className="action-btn-styled danger"
                  onClick={handleDeleteSelected}
                  style={{ borderColor: "rgba(139, 58, 42, 0.4)", color: "var(--rust)" }}
                >
                  🗑️ Delete
                </button>
              )}
            </div>
          </div>

          {/* ─── ANNOTATIONS GRID ─── */}
          <div>
            {filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "3rem 1rem", color: "var(--muted)" }}>
                No notes match your active search filter.
              </div>
            ) : (
              filtered.map((item) => (
                <div key={item.id} className="note-card" style={{
                  borderLeft: `4px solid ${item.type === "note" ? "var(--gold)" : "var(--sage)"}`
                }}>
                  {/* Left Column: Checkbox and Mini-Cover */}
                  <div className="card-left-col">
                    <input
                      type="checkbox"
                      className="note-checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => handleSelectToggle(item.id)}
                    />
                    <div className="card-cover-side" style={{
                      background: `linear-gradient(135deg, ${item.bookColorFrom}, ${item.bookColorTo})`
                    }}>
                      {item.bookTitle.slice(0, 16)}
                    </div>
                  </div>

                  {/* Right Column: Main Content */}
                  <div className="card-content-col">
                    {/* Header line */}
                    <div className="card-header-row">
                      <div className="card-meta-info">
                        <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>{item.bookTitle}</span>
                        <span className="card-meta-text">
                          by {item.bookAuthor} · Page {item.page}
                        </span>
                      </div>
                      
                      <div className="card-badge-row">
                        <span className="card-type-badge" style={{
                          background: item.type === "note" ? "rgba(200, 146, 42, 0.12)" : "rgba(74, 124, 89, 0.12)",
                          color: item.type === "note" ? "var(--gold)" : "var(--sage)",
                        }}>{item.type}</span>

                        <span className="card-date-text">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Highlighted Quote Content */}
                    <div className="quote-container">
                      "{item.text}"
                    </div>

                    {/* Notes Section */}
                    {editingId === item.id ? (
                      <div className="edit-container">
                        <textarea
                          className="edit-textarea"
                          value={editVal}
                          onChange={(e) => setEditVal(e.target.value)}
                          placeholder="Write your study notes here..."
                        />
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button
                            className="action-btn-styled primary"
                            onClick={() => saveEdit(item.bookId, item.id)}
                            style={{ padding: "6px 12px", fontSize: "0.75rem" }}
                          >
                            Save Note
                          </button>
                          <button
                            className="action-btn-styled"
                            onClick={() => setEditingId(null)}
                            style={{ padding: "6px 12px", fontSize: "0.75rem" }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginTop: "0.5rem" }}>
                        {item.note ? (
                          <div className="thoughts-container">
                            <strong>My Thoughts:</strong> {item.note}
                          </div>
                        ) : (
                          <span
                            onClick={() => startEditing(item.id, item.note)}
                            className="write-note-link"
                          >
                            + Write study notes / thoughts
                          </span>
                        )}
                      </div>
                    )}

                    {/* Footer Actions */}
                    <div className="card-footer-actions">
                      {item.note && editingId !== item.id && (
                        <button
                          onClick={() => startEditing(item.id, item.note)}
                          className="card-action-link"
                        >
                          ✏️ Edit Thoughts
                        </button>
                      )}
                      <button
                        onClick={() => handleJumpToPage(item.bookId, item.page)}
                        className="card-action-link"
                      >
                        📖 Jump to Reader
                      </button>
                      <button
                        onClick={() => handleDelete(item.bookId, item.id)}
                        className="card-action-link danger"
                      >
                        🗑️ Remove Note
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
